import { httpsCallable } from 'firebase/functions';
import { functions } from './firebase';
import { doc, getDoc, updateDoc, increment } from 'firebase/firestore';
import { db } from './firebase';

/**
 * Calcula a taxa do sistema baseado no plano da empresa
 * @param {string} plan - Plano da empresa (basic, pro, enterprise)
 * @param {number} transactionsThisMonth - Número de transações no mês atual
 * @returns {number} Percentual da taxa (ex: 3 para 3%)
 */
export const calculateSystemFee = (
  plan, 
  transactionsThisMonth = 0,
  systemFeeBasic = 3,
  systemFeePro = 2,
  systemFeeEnterprise = 1,
  enterpriseFreeTransactions = 10
) => {
  switch (plan?.toLowerCase()) {
    case 'basic':
      return systemFeeBasic;
    case 'pro':
      return systemFeePro;
    case 'enterprise':
      return transactionsThisMonth < enterpriseFreeTransactions ? 0 : systemFeeEnterprise;
    default:
      return systemFeeBasic; // Taxa padrão
  }
};

/**
 * Calcula o split de pagamento entre empresa, sistema e colaborador
 * @param {number} totalAmount - Valor total da transação
 * @param {number} commissionRate - Taxa de comissão do colaborador (%)
 * @param {number} systemFeeRate - Taxa do sistema (%)
 * @returns {Object} Objeto com os valores do split
 */
export const calculatePaymentSplit = (totalAmount, commissionRate = 0, systemFeeRate = 3) => {
  const systemFee = (totalAmount * systemFeeRate) / 100;
  const collaboratorCommission = (totalAmount * commissionRate) / 100;
  const companyAmount = totalAmount - systemFee - collaboratorCommission;

  return {
    total: totalAmount,
    systemFee: Math.round(systemFee * 100) / 100,
    collaboratorCommission: Math.round(collaboratorCommission * 100) / 100,
    companyAmount: Math.round(companyAmount * 100) / 100,
    systemFeeRate,
    commissionRate
  };
};

/**
 * Gera um link de pagamento do Mercado Pago com split
 * @param {Object} params - Parâmetros do pagamento
 * @returns {Promise<Object>} Link de pagamento e detalhes
 */
export const generatePaymentLink = async (params) => {
  const {
    companyId,
    reservationId,
    amount,
    description,
    clientEmail,
    clientName,
    commissionRate,
    collaboratorEmail,
    collaboratorName
  } = params;

  try {
    // Buscar informações da empresa
    const companyRef = doc(db, 'companies', companyId);
    const companyDoc = await getDoc(companyRef);
    
    if (!companyDoc.exists()) {
      throw new Error('Empresa não encontrada');
    }

    const companyData = companyDoc.data();
    const plan = companyData.plan || 'basic';
    const transactionsThisMonth = companyData.transactionsThisMonth || 0;
    const mercadoPagoAccessToken = companyData.mercadoPagoAccessToken;

    // Buscar configurações do sistema
    const systemConfigRef = doc(db, 'systemConfig', 'mercadoPago');
    const systemConfigDoc = await getDoc(systemConfigRef);
    const systemConfig = systemConfigDoc.exists() ? systemConfigDoc.data() : {};

    // Calcular taxas
    const enterpriseFree = systemConfig?.planFeatures?.enterprise?.freeTransactionsPerMonth ?? systemConfig.enterpriseFreeTransactions;

    const systemFeeRate = calculateSystemFee(
      plan, 
      transactionsThisMonth,
      systemConfig.systemFeeBasic,
      systemConfig.systemFeePro,
      systemConfig.systemFeeEnterprise,
      enterpriseFree
    );
    const split = calculatePaymentSplit(amount, commissionRate, systemFeeRate);

    // Chamar Cloud Function para criar o payment link
    const createPaymentLink = httpsCallable(functions, 'createMercadoPagoPaymentLink');
    
    const response = await createPaymentLink({
      companyId,
      reservationId,
      amount: split.total,
      description,
      clientEmail,
      clientName,
      split: {
        systemFee: split.systemFee,
        systemFeeRate: split.systemFeeRate,
        collaboratorCommission: split.collaboratorCommission,
        commissionRate: split.commissionRate,
        companyAmount: split.companyAmount,
        collaboratorEmail,
        collaboratorName
      },
      accessToken: mercadoPagoAccessToken
    });

    if (response.data.success) {
      // Atualizar contador de transações do mês (para plano Enterprise)
      if (plan === 'enterprise') {
        await updateDoc(companyRef, {
          transactionsThisMonth: increment(1),
          lastTransactionDate: new Date()
        });
      }

      return {
        success: true,
        paymentLink: response.data.paymentLink,
        paymentId: response.data.paymentId,
        qrCode: response.data.qrCode,
        qrCodeBase64: response.data.qrCodeBase64,
        split
      };
    } else {
      throw new Error(response.data.error || 'Erro ao gerar link de pagamento');
    }
  } catch (error) {
    console.error('Erro ao gerar link de pagamento:', error);
    return {
      success: false,
      error: error.message || 'Erro ao gerar link de pagamento'
    };
  }
};

/**
 * Verifica o status de um pagamento do Mercado Pago
 * @param {string} paymentId - ID do pagamento
 * @param {string} companyId - ID da empresa
 * @returns {Promise<Object>} Status do pagamento
 */
export const checkPaymentStatus = async (paymentId, companyId) => {
  try {
    const checkStatus = httpsCallable(functions, 'checkMercadoPagoPaymentStatus');
    
    const response = await checkStatus({
      paymentId,
      companyId
    });

    return response.data;
  } catch (error) {
    console.error('Erro ao verificar status do pagamento:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

/**
 * Reseta o contador de transações do mês (executar no início de cada mês)
 * @param {string} companyId - ID da empresa
 */
export const resetMonthlyTransactions = async (companyId) => {
  try {
    const companyRef = doc(db, 'companies', companyId);
    await updateDoc(companyRef, {
      transactionsThisMonth: 0,
      lastMonthReset: new Date()
    });
    return { success: true };
  } catch (error) {
    console.error('Erro ao resetar transações mensais:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Formata as informações de split para exibição
 * @param {Object} split - Objeto com informações do split
 * @returns {string} Texto formatado
 */
export const formatSplitInfo = (split) => {
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  return `
Total: ${formatCurrency(split.total)}
Taxa do Sistema (${split.systemFeeRate}%): ${formatCurrency(split.systemFee)}
Comissão Colaborador (${split.commissionRate}%): ${formatCurrency(split.collaboratorCommission)}
Valor da Empresa: ${formatCurrency(split.companyAmount)}
  `.trim();
};

const mercadoPagoService = {
  calculateSystemFee,
  calculatePaymentSplit,
  generatePaymentLink,
  checkPaymentStatus,
  resetMonthlyTransactions,
  formatSplitInfo
};

export default mercadoPagoService;
